import {Button, Card, Col, Row, Select, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import {getObjects, setObjects, setObjectsFilter, setWSObject} from "../../store/objectReducer"
import {Loading} from "../../components/Loading/Loading"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {useHistory, useLocation} from "react-router-dom"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {FormSelect} from "../../components/Form/FormSelect"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {getObjectSecurityType} from "../../helps/scheduleHelper"
import {useGetCompanies, useGetObjectGroups} from "../../hooks/useFetchHooks"
import {ObjectSecurityType} from "../../models/response"
import {FormInput} from "../../components/Form/FormInput"

export const Objects = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const objects = useSelector((state: StateType) => state.objectReducer.objects)
    const filter = useSelector((state: StateType) => state.objectReducer.filter)
    const ws = useSelector((state: StateType) => state.objectReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)

    const objectGroupsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany])
    const objectGroups = useGetObjectGroups(objectGroupsQuery)

    const columns = useMemo(() => {
        const cols = []

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: 'Компания',
            dataIndex: 'nameCompany',
            key: 'nameCompany',
            align: center,
        })

        cols.push(
            {
                title: 'Группа',
                dataIndex: 'nameObjectGroup',
                key: 'nameObjectGroup',
                align: center,
            },
            {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                align: center,
            },
            {
                title: 'Адрес',
                dataIndex: 'address',
                key: 'address',
                align: center,
            },
            {
                title: 'Статус охраны',
                dataIndex: 'security',
                key: 'security',
                render: (security: {securityActive: boolean, securityType: ObjectSecurityType}) => {
                    return (
                        <div className="table-cell-status">
                            <Tag color={security?.securityActive ? 'green' : 'volcano'} style={{marginRight: 0}}>{security?.securityActive ? 'Активно' : 'Не активно'}</Tag>
                            <Tag color="default" style={{marginRight: 0}}>{getObjectSecurityType(security.securityType)}</Tag>
                        </div>
                    )
                },
                align: center,
            },
            {
                title: 'Статус',
                dataIndex: 'active',
                key: 'active',
                render: (active: boolean) => {
                    return (
                        <Tag color={active ? 'green' : 'volcano'} style={{marginRight: 0}}>{active ? 'Активно' : 'Не активно'}</Tag>
                    )
                },
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setObjectsFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getObjectsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (location?.search) {
            dispatch(getObjects({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() => {
        getObjectsFunc()

        return () => {
            dispatch(setObjects(null))
        }
    }, [dispatch, getObjectsFunc])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() => {
        if (profile?.idCompany !== ID_SYSTEM_COMPANY && filter?.idCompany !== profile?.idCompany) {
            history.replace({search: filterToString({...filter, idCompany: profile?.idCompany})})
        }
    }, [profile])

    useEffect(() => {
        if (ws) {
            getObjectsFunc(true)
        }
        dispatch(setWSObject(null))
    }, [dispatch, ws, getObjectsFunc])

    const data = useMemo(() => objects?.data?.map(i => {
        return {
            key: i.id,
            id: i.id,
            nameObjectGroup: i.nameObjectGroup,
            nameCompany: i.nameCompany,
            name: i.name,
            address: i.address,
            attributes: i.attributes,
            active: i.active,
            deleted: i.deleted,
            security: {
                securityActive: i.securityActive,
                securityType: i.securityType,
            },
        }
    }), [objects])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])
    const objectGroupsOptions = useMemo(() => objectGroups?.data?.map(i => ({label: i.name, value: i.id})), [objectGroups])

    return (
        <div className="tabled">
            <Helmet>
                <title>Объекты</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className='table-title'>
                                  <div>Объекты</div>
                                  <Button onClick={() => history.push('/objectForm')} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className='table-title-extra'>
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ? <FormSelect value={filter?.idCompany}
                                                        containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                        dropdownStyle={{minWidth: 'min-content'}}
                                                        onChange={(value) => history.replace({search: filterToString({...filter, idCompany: value, idObjectGroup: '', offset: 0})})}
                                                        options={companyOptions}
                                                        placeholder="Компания"
                                                        allowClear
                                                        placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.idObjectGroup}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, idObjectGroup: value, offset: 0})})}
                                              options={objectGroupsOptions}
                                              placeholder="Группа"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.status}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                              options={isActiveOptionsSelect}
                                              placeholder="Статус"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: 'auto'}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                objects
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter.limit ? {
                                                 position: objects?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: objects.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) => {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: () => history.push(`/object/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/object/${id}`, '_blank', 'noopener,noreferrer')
                                             })}
                                             rowClassName={({deleted}) => deleted ? 'table-row-deleted' : ''}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
